import { useEffect, useState } from 'react';
import {
  TrendingUp,
  IndianRupee,
  ShoppingBag,
  Users,
  Clock,
  Star,
  Calendar,
  ArrowUp,
  ArrowDown,
  ChefHat,
  Package,
  BarChart3,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { api } from '../../lib/api';

interface AnalyticsProps {
  restaurantId: string;
  currencySymbol?: string;
  currencyDecimals?: number;
}

type DateRange = 'today' | 'week' | 'month' | 'year';

type AnalyticsData = {
  range: DateRange;
  period: {
    currentStart: string;
    currentEnd: string;
    previousStart: string;
    previousEnd: string;
  };
  metrics: {
    totalRevenue: number;
    totalOrders: number;
    averageOrderValue: number;
    uniqueCustomers: number;
    repeatRate: number;
    completionRate: number;
    activeMenuItems: number;
    revenueGrowth: number;
    orderGrowth: number;
    averageRating: number;
    totalReviews: number;
  };
  charts: {
    revenueTrend: Array<{ name: string; revenue: number; orders: number }>;
    orderStatusDistribution: Array<{ key: string; name: string; value: number; color: string }>;
    peakHours: Array<{ hour: string; orders: number }>;
    topSellingItems: Array<{ id: string; name: string; quantity: number; revenue: number }>;
  };
};

const emptyAnalyticsData = (range: DateRange): AnalyticsData => ({
  range,
  period: {
    currentStart: new Date().toISOString(),
    currentEnd: new Date().toISOString(),
    previousStart: new Date().toISOString(),
    previousEnd: new Date().toISOString(),
  },
  metrics: {
    totalRevenue: 0,
    totalOrders: 0,
    averageOrderValue: 0,
    uniqueCustomers: 0,
    repeatRate: 0,
    completionRate: 0,
    activeMenuItems: 0,
    revenueGrowth: 0,
    orderGrowth: 0,
    averageRating: 0,
    totalReviews: 0,
  },
  charts: {
    revenueTrend: [],
    orderStatusDistribution: [],
    peakHours: [],
    topSellingItems: [],
  },
});

export function Analytics({
  restaurantId,
  currencySymbol = '₹',
  currencyDecimals = 2,
}: AnalyticsProps) {
  const [dateRange, setDateRange] = useState<DateRange>('week');
  const [analytics, setAnalytics] = useState<AnalyticsData>(emptyAnalyticsData('week'));
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadAnalytics = async () => {
      if (!restaurantId) return;
      setLoading(true);
      setLoadError('');

      try {
        const response = await api.restaurants.getAnalytics(restaurantId, { range: dateRange });
        if (!isMounted) return;
        setAnalytics(response?.data || emptyAnalyticsData(dateRange));
      } catch (error: any) {
        if (!isMounted) return;
        setAnalytics(emptyAnalyticsData(dateRange));
        setLoadError(error?.message || 'Failed to load analytics');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void loadAnalytics();

    return () => {
      isMounted = false;
    };
  }, [dateRange, restaurantId]);

  const { metrics, charts } = analytics;
  const resolvedDecimals = Math.max(
    2,
    Number.isFinite(Number(currencyDecimals)) ? Number(currencyDecimals) : 2
  );
  const formatMoney = (value: number | string) => {
    const amount = Number(value ?? 0);
    const safe = Number.isFinite(amount) ? amount : 0;
    return `${currencySymbol}${safe.toLocaleString('en-IN', {
      minimumFractionDigits: resolvedDecimals,
      maximumFractionDigits: resolvedDecimals,
    })}`;
  };
  const topSellingItems = charts.topSellingItems;
  const topItemQuantity = topSellingItems[0]?.quantity || 1;

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl text-gray-900 font-bold">Analytics & Reports</h1>
          <p className="text-gray-600 text-sm mt-1">Live performance insights for your restaurant</p>
          {loading && <p className="text-xs text-gray-500 mt-1">Refreshing analytics...</p>}
          {!loading && loadError && <p className="text-xs text-red-500 mt-1">{loadError}</p>}
        </div>

        <div className="flex gap-2 bg-white rounded-lg p-1 shadow-sm">
          {(['today', 'week', 'month', 'year'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateRange === range ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {range.charAt(0).toUpperCase() + range.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <IndianRupee className="w-6 h-6" />
            </div>
            <div className={`flex items-center gap-1 text-sm ${metrics.revenueGrowth >= 0 ? 'text-green-100' : 'text-red-100'}`}>
              {metrics.revenueGrowth >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
              {Math.abs(metrics.revenueGrowth).toFixed(1)}%
            </div>
          </div>
          <h3 className="text-sm font-medium text-green-100 mb-1">Total Revenue</h3>
          <p className="text-3xl font-bold">{formatMoney(metrics.totalRevenue)}</p>
          <p className="text-xs text-green-100 mt-2">vs previous {dateRange}</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-6 h-6" />
            </div>
            <div className={`flex items-center gap-1 text-sm ${metrics.orderGrowth >= 0 ? 'text-blue-100' : 'text-red-100'}`}>
              {metrics.orderGrowth >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
              {Math.abs(metrics.orderGrowth).toFixed(1)}%
            </div>
          </div>
          <h3 className="text-sm font-medium text-blue-100 mb-1">Total Orders</h3>
          <p className="text-3xl font-bold">{metrics.totalOrders}</p>
          <p className="text-xs text-blue-100 mt-2">vs previous {dateRange}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6" />
            </div>
          </div>
          <h3 className="text-sm font-medium text-purple-100 mb-1">Avg Order Value</h3>
          <p className="text-3xl font-bold">{formatMoney(metrics.averageOrderValue)}</p>
          <p className="text-xs text-purple-100 mt-2">Per order average</p>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6" />
            </div>
          </div>
          <h3 className="text-sm font-medium text-orange-100 mb-1">Unique Customers</h3>
          <p className="text-3xl font-bold">{metrics.uniqueCustomers}</p>
          <p className="text-xs text-orange-100 mt-2">{metrics.repeatRate.toFixed(1)}% repeat rate</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg text-gray-900 font-semibold">Revenue Trend</h3>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          {charts.revenueTrend.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-gray-500">No revenue trend data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={charts.revenueTrend}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" stroke="#6B7280" fontSize={12} />
                <YAxis stroke="#6B7280" fontSize={12} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                />
                <Area type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} fill="url(#colorRevenue)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg text-gray-900 font-semibold">Order Status</h3>
            <Package className="w-5 h-5 text-blue-600" />
          </div>
          {charts.orderStatusDistribution.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-gray-500">No order status data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={charts.orderStatusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={100}
                  dataKey="value"
                >
                  {charts.orderStatusDistribution.map((entry) => (
                    <Cell key={entry.key} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg text-gray-900 font-semibold">Peak Hours</h3>
            <Clock className="w-5 h-5 text-orange-600" />
          </div>
          {charts.peakHours.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-gray-500">No peak hour data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={charts.peakHours}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="hour" stroke="#6B7280" fontSize={12} />
                <YAxis stroke="#6B7280" fontSize={12} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px' }}
                />
                <Bar dataKey="orders" fill="#F59E0B" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg text-gray-900 font-semibold">Top Selling Items</h3>
            <ChefHat className="w-5 h-5 text-purple-600" />
          </div>
          <div className="space-y-4">
            {topSellingItems.length === 0 && (
              <p className="text-gray-500 text-center py-8">No sales data available</p>
            )}
            {topSellingItems.map((item, index) => (
              <div key={item.id} className="flex items-center gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="text-gray-900 font-medium">{item.name}</p>
                  <p className="text-sm text-gray-600">
                    {item.quantity} sold - {formatMoney(item.revenue)}
                  </p>
                </div>
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full"
                    style={{ width: `${Math.max((item.quantity / topItemQuantity) * 100, 8)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
              <Star className="w-5 h-5 text-yellow-600" />
            </div>
            <h3 className="text-gray-900 font-semibold">Average Rating</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-2">
            {metrics.averageRating > 0 ? metrics.averageRating.toFixed(1) : 'N/A'}
          </p>
          <p className="text-sm text-gray-600">Based on {metrics.totalReviews} reviews in this period</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-gray-900 font-semibold">Completion Rate</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-2">{metrics.completionRate.toFixed(1)}%</p>
          <p className="text-sm text-gray-600">
            Delivered and completed orders for selected {dateRange}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-gray-900 font-semibold">Menu Performance</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-2">{metrics.activeMenuItems}</p>
          <p className="text-sm text-gray-600">Active menu items available for sale</p>
        </div>
      </div>
    </div>
  );
}
